import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
} from '@angular/core';
import { BlockNodes, NodeGroup } from '@principle-theorem/editor';
import {
  EditorNode,
  EditorNodeComponent,
  NodeAttribute,
  type IDomSerialising,
  type IHasUid,
} from '@principle-theorem/ng-prosemirror';
import { BasicDialogService } from '@principle-theorem/ng-shared';
import {
  type DOMOutputSpec,
  type Node as ProsemirrorNode,
} from '@tiptap/pm/model';
import { ReplaySubject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import {
  RawHtmlDialogComponent,
  createRawHtmlDialogConfig,
  type IRawHtmlDialogData,
  type IRawHtmlDialogOutput,
} from '../raw-html-dialog/raw-html-dialog.component';
import { HTMLSanitiser } from '../sanitise-raw-html';

export interface IEditorRawHtmlAttributes extends IHasUid {
  html: string;
}

@EditorNode({
  name: BlockNodes.RawHtml,
  group: NodeGroup.Block,
  inline: false,
  atom: true,
  draggable: true,
  isolating: true,
  selectable: true,
  parseHTML: () => [
    {
      tag: 'div[data-raw-html]',
      getAttrs: (dom: unknown) =>
        dom instanceof HTMLElement ? { html: dom.innerHTML } : false,
    },
  ],
})
@Component({
  selector: 'pt-raw-html-node',
  templateUrl: './raw-html-node.component.html',
  host: { class: 'block max-w-full' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RawHtmlNodeComponent
  extends EditorNodeComponent<IEditorRawHtmlAttributes>
  implements IHasUid, IDomSerialising, IEditorRawHtmlAttributes
{
  private _html = '';
  html$ = new ReplaySubject<string>(1);

  @NodeAttribute()
  @Input()
  uid: string = uuid();

  constructor(
    private _dialog: BasicDialogService,
    elementRef: ElementRef
  ) {
    super(elementRef);
  }

  @NodeAttribute()
  @Input()
  set html(html: string) {
    this._html = html ?? '';
    this.html$.next(this._html);
  }

  get html(): string {
    return this._html;
  }

  async edit(): Promise<void> {
    const result = await this._dialog
      .open<RawHtmlDialogComponent, IRawHtmlDialogData, IRawHtmlDialogOutput>(
        RawHtmlDialogComponent,
        createRawHtmlDialogConfig({ html: this._html })
      )
      .afterClosed()
      .toPromise();

    if (!result) {
      return;
    }
    this.update.next({ html: result.html, uid: this.uid });
  }

  /**
   * Serialises the stored HTML verbatim. This is what
   * `versionedSchemaToHtml` emits into outgoing emails, so the customer's
   * marketing HTML survives the editor schema untouched.
   */
  renderHTML(data: { node: ProsemirrorNode }): DOMOutputSpec {
    const container = document.createElement('div');
    container.setAttribute('data-raw-html', 'true');
    container.innerHTML = HTMLSanitiser.stripActiveContent(
      String(data.node.attrs.html ?? '')
    );
    return container;
  }
}
